import { webappDisplayUrl } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button } from "@sagot/ui";
import { useRef } from "react";
import { slugify } from "../lib/slug";
import { FormAlert } from "./components/form-alert";
import { DoneTick, GlobeGlyph } from "./register-icons";
import { PaneTitle, usePaneFocus } from "./register-pane";

const PLACEHOLDER_SLUG = "your-workspace";

const NEXT_STEPS = [
  { title: "auth.register.doneStep1", hint: "auth.register.doneStep1Hint", tone: "blue" },
  { title: "auth.register.doneStep2", hint: "auth.register.doneStep2Hint", tone: "plain" },
  { title: "auth.register.doneStep3", hint: "auth.register.doneStep3Hint", tone: "plain" },
] as const;

interface RegisterDonePaneProps {
  /** Owner's full name: the greeting uses the first word. Empty after a refresh. */
  name: string;
  orgName: string;
  opening: boolean;
  error: string | null;
  onOpen: () => void;
}

/** Last pane (mockup pane 6): the tick, the workspace address, the three first steps and Open the console. */
export function RegisterDonePane({ name, orgName, opening, error, onOpen }: RegisterDonePaneProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  usePaneFocus(ref);
  const first = name.trim().split(/\s+/)[0] ?? "";
  const slug = slugify(orgName) || PLACEHOLDER_SLUG;
  return (
    <div ref={ref} className="r-pane">
      <div className="r-done">
        <div className="ck">
          <DoneTick />
        </div>
        <PaneTitle>{first ? t("auth.register.doneWelcome", { name: first }) : t("auth.register.doneTitle")}</PaneTitle>
        <div className="r-url">
          <GlobeGlyph />
          <span>{webappDisplayUrl(slug)}</span>
        </div>
        <ol className="next" aria-label={t("auth.register.doneStepsLabel")}>
          {NEXT_STEPS.map((step, index) => (
            <li key={step.title}>
              <span className="n">{index + 1}</span>
              <span>
                {t(step.title)}
                <small>{t(step.hint)}</small>
              </span>
              <span className="r-pill" data-tone={step.tone}>
                {t("auth.register.doneEta")}
              </span>
            </li>
          ))}
        </ol>
        {error ? <FormAlert title={error} /> : null}
        <Button type="button" size="lg" className="w-full" loading={opening} disabled={opening} onClick={onOpen}>
          {t("auth.register.openConsole")}
        </Button>
        <p className="r-fine r-fine-end">{t("auth.register.trialStarted")}</p>
      </div>
    </div>
  );
}
